/// <amd-module name="Build/Admin/Common/Stores/JobStore" />
import { Store } from "VSS/Reflux/Store";

import { caseInsensitiveContains, equals } from "VSS/Core/Util/String";
import { MakeOptional } from "VSS/Core/Util/Types";

import { Filter } from "VSSUI/Utilities/Filter";

import { ITeamProject } from "TFS/Services/TfsProjectService";

import { TaskAgentJobRequest } from "DistributedTask/Client/Contracts/TaskAgent";

import { FilterKeys } from "Build/Admin/Common/Constants";
import { getAgentSpecificationName, getJobNumber, getProjectName } from "Build/Admin/Common/TaskAgentJobRequestUtils";

export class JobStore extends Store {
    public state: TaskAgentJobRequest[] = [];
    public filter: Filter = new Filter();

    constructor() {
        super("JobStoreChanged");
    }

    public updateJobs = (jobs: TaskAgentJobRequest[]) => {
        this.state = this._sortJobs(jobs);
        this.emitChanged();
    }

    public updateJob = (job: TaskAgentJobRequest) => {
        let matchingJob = this.state.filter(j => j.requestId === job.requestId)[0];
        const nonMatchingJobs = this.state.filter(j => j.requestId !== job.requestId);

        if (matchingJob) {
            // We get partial object data on some SignalR events, so don't blank out existing properties if the new ones are undefined
            matchingJob = {
                definition: job.definition || matchingJob.definition,
                owner: job.owner ||  matchingJob.owner,
                ...(job as MakeOptional<TaskAgentJobRequest, "definition" | "owner">)
            };
            nonMatchingJobs.push(matchingJob);
        }
        else {
            nonMatchingJobs.push(job);
        }

        this.state = this._sortJobs(nonMatchingJobs);

        this.emitChanged();
    }

    public getFilteredJobs(projects: ITeamProject[], includeAgentAsKeyword: boolean): TaskAgentJobRequest[] {
        if (this.filter.hasChangesToReset()) {
            const keyword: string | undefined = this.filter.getFilterItemValue(FilterKeys.Keyword);
            const projectIds: string[] | undefined = this.filter.getFilterItemValue(FilterKeys.Project);
            const agentNames: string[] | undefined = this.filter.getFilterItemValue(FilterKeys.Agent);
            const agentSpecIdentifiers: string[] | undefined = this.filter.getFilterItemValue(FilterKeys.AgentSpec);
            return this.state.filter(job => {
                let includeJob: boolean = true;

                if (job) {
                    if (includeJob && agentNames) {
                        includeJob = this._filterByAgent(job, agentNames);
                    }
                    if (includeJob && agentSpecIdentifiers) {
                        includeJob = this._filterByAgentSpec(job, agentSpecIdentifiers);
                    }
                    if (includeJob && projectIds) {
                        includeJob = this._filterByProject(job, projectIds);
                    }
                    if (includeJob && keyword) {
                        includeJob = this._filterByKeyword(job, keyword, projects, includeAgentAsKeyword);
                    }
                }
                else {
                    includeJob = false;
                }

                return includeJob;
            });
        }
        else {
            return this.state;
        }
    }

    private _filterByAgent(job: TaskAgentJobRequest, agentNames: string[]): boolean {
        // If no agent names are given, then do not filter out any jobs
        if (agentNames.length === 0) {
            return true;
        }

        for (const agentName of agentNames) {
            if (job.reservedAgent &&
                equals(job.reservedAgent.name, agentName, false)) {
                return true;
            }
        }

        return false;
    }

    private _filterByAgentSpec(job: TaskAgentJobRequest, agentSpecIdentifiers: string[]): boolean {
        // If no agent specification identifiers are given, then do not filter out any jobs
        if (agentSpecIdentifiers.length === 0) {
            return true;
        }

        const agentSpecName: string = getAgentSpecificationName(job);
        for (const agentSpecIdentifier of agentSpecIdentifiers) {
            if (equals(agentSpecName, agentSpecIdentifier, false)) {
                return true;
            }
        }

        return false;
    }

    private _filterByProject(job: TaskAgentJobRequest, projectIds: string[]): boolean {
        // If no Project Ids are given, then do not filter out any jobs
        if (projectIds.length === 0) {
            return true;
        }

        for (const projectId of projectIds) {
            if (equals(job.scopeId, projectId, true)) {
                return true;
            }
        }
        return false;
    }

    private _filterByKeyword(job: TaskAgentJobRequest, keyword: string, projects: ITeamProject[], includeAgentAsKeyword: boolean): boolean {
        const jobNumber: string = getJobNumber(job);
        const projectName: string = getProjectName(job, projects);
        const agentSpecName: string = getAgentSpecificationName(job);

        return caseInsensitiveContains(jobNumber, keyword) ||
            (job.owner && caseInsensitiveContains(job.owner.name, keyword)) ||
            (job.jobName && caseInsensitiveContains(job.jobName, keyword)) ||
            (job.definition && caseInsensitiveContains(job.definition.name, keyword)) ||
            (includeAgentAsKeyword && job.reservedAgent && caseInsensitiveContains(job.reservedAgent.name, keyword)) ||
            caseInsensitiveContains(projectName, keyword) ||
            (includeAgentAsKeyword && caseInsensitiveContains(agentSpecName, keyword));
    }

    private _sortJobs(jobs: TaskAgentJobRequest[]): TaskAgentJobRequest[] {
        const queuedRequests: TaskAgentJobRequest[] = [];
        const assignedRequests: TaskAgentJobRequest[] = [];
        const runningRequests: TaskAgentJobRequest[] = [];
        const finishedRequests: TaskAgentJobRequest[] = [];

        for (const job of jobs) {
            if (job.finishTime) {
                finishedRequests.push(job);
            }
            else if (job.receiveTime) {
                runningRequests.push(job);
            }
            else if (job.assignTime) {
                assignedRequests.push(job);
            }
            else {
                queuedRequests.push(job);
            }
        }

        return runningRequests.sort((a, b) => {
            return a.receiveTime.getTime() - b.receiveTime.getTime();
        }).concat(assignedRequests.sort((a, b) => {
            return a.assignTime.getTime() - b.assignTime.getTime();
        })).concat(queuedRequests.sort((a, b) => {
            return a.queueTime.getTime() - b.queueTime.getTime();
        })).concat(finishedRequests.sort((a, b) => {
            // Finished requests go in descending order
            return b.finishTime.getTime() - a.finishTime.getTime();
        }));
    }
}
